import React, { useState, useEffect } from 'react';
import './Applications.css';

function Applications() {
  const [activeTab, setActiveTab] = useState('advisory');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Store counts for each tab
  const [tabCounts, setTabCounts] = useState({
    advisory: 0,
    business: 0,
    delivery: 0,
    employee: 0
  });

  const tabs = [
    { id: 'advisory', label: 'Advisory Partners' },
    { id: 'business', label: 'Business Partners' },
    { id: 'delivery', label: 'Delivery Partners' },
    { id: 'employee', label: 'Employee Partners' }
  ];

  useEffect(() => {
    fetchApplications();
    fetchAllCounts(); // Fetch counts for all tabs
  }, [activeTab]);

  // Fetch counts for all application types
  const fetchAllCounts = async () => {
    const token = localStorage.getItem('adminToken');
    const types = ['advisory', 'business', 'delivery', 'employee'];
    
    for (const type of types) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/applications/${type}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setTabCounts(prev => ({ ...prev, [type]: data.data.length }));
        }
      } catch (error) {
        console.error(`Error fetching ${type} count:`, error);
      }
    }
  };

  const fetchApplications = async () => {
    const token = localStorage.getItem('adminToken');
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/applications/${activeTab}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setApplications(data.data);
        // Update count for current tab
        setTabCounts(prev => ({ ...prev, [activeTab]: data.data.length }));
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/applications/${activeTab}/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        alert('Status updated successfully');
        fetchApplications();
        fetchAllCounts(); // Refresh counts after status change
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Update failed');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/applications/${activeTab}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Application deleted successfully');
        fetchApplications();
        fetchAllCounts(); // Refresh counts after deletion
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Delete failed');
    }
  };

  const viewDetails = async (id) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/applications/${activeTab}/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedApp(data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching details:', error);
    }
  };

  const openFileViewer = (fileUrl, fileName, fileType) => {
    setSelectedFile({ url: fileUrl, name: fileName, type: fileType });
    setShowFileViewer(true);
  };

  const closeFileViewer = () => {
    setShowFileViewer(false);
    setSelectedFile(null);
  };

  const getStatusOptions = () => {
    if (activeTab === 'employee') {
      return ['pending', 'reviewed', 'shortlisted', 'accepted', 'rejected', 'hired'];
    }
    return ['pending', 'reviewed', 'accepted', 'rejected'];
  };

  const getFileUrl = (fileData) => {
    if (fileData?.cloudinaryUrl) return fileData.cloudinaryUrl;
    if (fileData?.url) return fileData.url;
    return null;
  };

  const renderDetails = () => {
    if (!selectedApp) return null;
    
    if (activeTab === 'advisory') {
      return (
        <>
          <h3>Personal Information</h3>
          <div className="details-grid">
            <p><strong>Full Name:</strong> {selectedApp.fullName}</p>
            <p><strong>Email:</strong> {selectedApp.email}</p>
            <p><strong>Phone:</strong> {selectedApp.contactNumber}</p>
            <p><strong>Organization:</strong> {selectedApp.organization || 'N/A'}</p>
            <p><strong>LinkedIn:</strong> {selectedApp.linkedin || 'N/A'}</p>
          </div>
          <h3>Professional Background</h3>
          <div className="details-grid">
            <p><strong>Years of Experience:</strong> {selectedApp.yearsExp}</p>
            <p><strong>Expertise:</strong> {selectedApp.expertise?.join(', ') || 'N/A'}</p>
            <p><strong>Industries:</strong> {selectedApp.industries?.join(', ') || 'N/A'}</p>
          </div>
          <h3>Engagement Preferences</h3>
          <div className="details-grid">
            <p><strong>AI Experience:</strong> {selectedApp.aiExperience}</p>
            <p><strong>AI Level:</strong> {selectedApp.aiLevel}</p>
            <p><strong>Involvement:</strong> {selectedApp.involvement}</p>
            <p><strong>Commercial Model:</strong> {selectedApp.commercialModel}</p>
          </div>
          {selectedApp.bio && (selectedApp.bio.cloudinaryUrl || selectedApp.bio.filename) && (
            <div className="file-section">
              <h3>Uploaded Bio Document</h3>
              <button 
                className="view-file-btn"
                onClick={() => openFileViewer(
                  getFileUrl(selectedApp.bio) || `/uploads/bios/${selectedApp.bio.filename}`,
                  selectedApp.bio.originalName || selectedApp.bio.filename,
                  selectedApp.bio.mimetype
                )}
              >
                📄 View Bio Document
              </button>
            </div>
          )}
        </>
      );
    }
    
    if (activeTab === 'business') {
      return (
        <>
          <h3>Contact Information</h3>
          <div className="details-grid">
            <p><strong>Name:</strong> {selectedApp.fullName}</p>
            <p><strong>Email:</strong> {selectedApp.email}</p>
            <p><strong>Phone:</strong> {selectedApp.contactNumber}</p>
            <p><strong>Website:</strong> {selectedApp.website}</p>
          </div>
          <h3>Business Overview</h3>
          <div className="details-grid">
            <p><strong>Organization Type:</strong> {selectedApp.orgType}</p>
            <p><strong>Focus Areas:</strong> {selectedApp.focusAreas?.join(', ') || 'N/A'}</p>
            <p><strong>Scale:</strong> {selectedApp.scale}</p>
          </div>
          <h3>Partnership Preferences</h3>
          <div className="details-grid">
            <p><strong>Partnership Type:</strong> {selectedApp.partnershipType?.join(', ') || 'N/A'}</p>
            <p><strong>B2B Experience:</strong> {selectedApp.b2bExp}</p>
            <p><strong>Collaboration Model:</strong> {selectedApp.collabModel}</p>
            <p><strong>Expected Outcomes:</strong> {selectedApp.expectedOutcome?.join(', ') || 'N/A'}</p>
          </div>
          {selectedApp.bio && (selectedApp.bio.cloudinaryUrl || selectedApp.bio.filename) && (
            <div className="file-section">
              <h3>Uploaded Company Bio</h3>
              <button 
                className="view-file-btn"
                onClick={() => openFileViewer(
                  getFileUrl(selectedApp.bio) || `/uploads/business-bios/${selectedApp.bio.filename}`,
                  selectedApp.bio.originalName || selectedApp.bio.filename,
                  selectedApp.bio.mimetype
                )}
              >
                📄 View Company Bio
              </button>
            </div>
          )}
        </>
      );
    }
    
    if (activeTab === 'delivery') {
      return (
        <>
          <h3>Contact Information</h3>
          <div className="details-grid">
            <p><strong>Name:</strong> {selectedApp.fullName}</p>
            <p><strong>Email:</strong> {selectedApp.email}</p>
            <p><strong>Phone:</strong> {selectedApp.contactNumber}</p>
            <p><strong>Company:</strong> {selectedApp.companyName || 'N/A'}</p>
            <p><strong>Website:</strong> {selectedApp.website}</p>
          </div>
          <h3>Capabilities</h3>
          <div className="details-grid">
            <p><strong>Organization Type:</strong> {selectedApp.orgType}</p>
            <p><strong>Capabilities:</strong> {selectedApp.capabilities?.join(', ') || 'N/A'}</p>
            <p><strong>Team Size:</strong> {selectedApp.teamSize}</p>
            <p><strong>Industries:</strong> {selectedApp.industries?.join(', ') || 'N/A'}</p>
          </div>
          <h3>Technical Expertise</h3>
          <div className="details-grid">
            <p><strong>Tech Expertise:</strong> {selectedApp.techExpertise?.join(', ') || 'N/A'}</p>
            <p><strong>Delivery Maturity:</strong> {selectedApp.deliveryMaturity}</p>
            <p><strong>Support:</strong> {selectedApp.support}</p>
            <p><strong>Engagement Model:</strong> {selectedApp.engagementModel}</p>
            <p><strong>Capacity:</strong> {selectedApp.capacity}</p>
          </div>
          {selectedApp.portfolio && (selectedApp.portfolio.cloudinaryUrl || selectedApp.portfolio.filename) && (
            <div className="file-section">
              <h3>Uploaded Portfolio</h3>
              <button 
                className="view-file-btn"
                onClick={() => openFileViewer(
                  getFileUrl(selectedApp.portfolio) || `/uploads/delivery-portfolios/${selectedApp.portfolio.filename}`,
                  selectedApp.portfolio.originalName || selectedApp.portfolio.filename,
                  selectedApp.portfolio.mimetype
                )}
              >
                📄 View Portfolio
              </button>
            </div>
          )}
        </>
      );
    }
    
    if (activeTab === 'employee') {
      return (
        <>
          <h3>Personal Information</h3>
          <div className="details-grid">
            <p><strong>Name:</strong> {selectedApp.fullName}</p>
            <p><strong>Email:</strong> {selectedApp.email}</p>
            <p><strong>Phone:</strong> {selectedApp.contactNumber}</p>
            <p><strong>Current Role:</strong> {selectedApp.currentRole}</p>
            <p><strong>Current Company:</strong> {selectedApp.currentCompany || 'N/A'}</p>
            <p><strong>LinkedIn:</strong> {selectedApp.linkedin || 'N/A'}</p>
          </div>
          <h3>Professional Background</h3>
          <div className="details-grid">
            <p><strong>Years of Experience:</strong> {selectedApp.yearsOfExperience}</p>
            <p><strong>Expertise:</strong> {selectedApp.expertise?.join(', ') || 'N/A'}</p>
            <p><strong>Skills:</strong> {selectedApp.skills?.join(', ') || 'N/A'}</p>
          </div>
          <h3>Partnership Preferences</h3>
          <div className="details-grid">
            <p><strong>Interest Area:</strong> {selectedApp.interestArea}</p>
            <p><strong>Availability:</strong> {selectedApp.availability}</p>
            <p><strong>Expected Compensation:</strong> {selectedApp.expectedCompensation || 'N/A'}</p>
            <p><strong>Preferred Location:</strong> {selectedApp.preferredLocation || 'N/A'}</p>
            <p><strong>Work Mode:</strong> {selectedApp.workMode}</p>
            <p><strong>Portfolio:</strong> {selectedApp.portfolio || 'N/A'}</p>
            <p><strong>GitHub:</strong> {selectedApp.github || 'N/A'}</p>
          </div>
          {selectedApp.resume && (selectedApp.resume.cloudinaryUrl || selectedApp.resume.filename) && (
            <div className="file-section">
              <h3>Uploaded Resume</h3>
              <button 
                className="view-file-btn"
                onClick={() => openFileViewer(
                  getFileUrl(selectedApp.resume) || `/uploads/employee-resumes/${selectedApp.resume.filename}`,
                  selectedApp.resume.originalName || selectedApp.resume.filename,
                  selectedApp.resume.mimetype
                )}
              >
                📄 View Resume
              </button>
            </div>
          )}
        </>
      );
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="applications-admin">
      <div className="admin-header">
        <h1>Manage Applications</h1>
      </div>

      <div className="tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label} ({tabCounts[tab.id]})
          </button>
        ))}
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Company/Role</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app) => (
              <tr key={app._id}>
                <td>{app.fullName}</td>
                <td>{app.email}</td>
                <td>{app.contactNumber}</td>
                <td>{app.companyName || app.currentRole || app.orgType || '-'}</td>
                <td>{new Date(app.submittedAt).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge status-${app.status}`}>{app.status}</span>
                </td>
                <td>
                  <button className="btn-view" onClick={() => viewDetails(app._id)}>View</button>
                  <select
                    value={app.status}
                    onChange={(e) => updateStatus(app._id, e.target.value)}
                    className="status-select"
                  >
                    {getStatusOptions().map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <button className="btn-delete" onClick={() => deleteApplication(app._id)}>Delete</button>
                </td>
              </tr>
            ))}
            {applications.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>No applications found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for full details */}
      {showModal && selectedApp && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Application Details - {selectedApp.fullName}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {renderDetails()}
              <div className="modal-notes">
                <h3>Admin Notes</h3>
                <p><strong>Status:</strong> {selectedApp.status}</p>
                <p><strong>Submitted:</strong> {new Date(selectedApp.submittedAt).toLocaleString()}</p>
                {selectedApp.reviewedAt && <p><strong>Reviewed:</strong> {new Date(selectedApp.reviewedAt).toLocaleString()}</p>}
                {selectedApp.reviewedBy && <p><strong>Reviewed By:</strong> {selectedApp.reviewedBy}</p>}
                {selectedApp.notes && <p><strong>Notes:</strong> {selectedApp.notes}</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iframe File Viewer Modal */}
      {showFileViewer && selectedFile && (
        <div className="file-viewer-overlay" onClick={closeFileViewer}>
          <div className="file-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="file-viewer-header">
              <h3>Viewing: {selectedFile.name}</h3>
              <button className="file-viewer-close" onClick={closeFileViewer}>&times;</button>
            </div>
            <div className="file-viewer-body">
              {selectedFile.type === 'application/pdf' || selectedFile.name?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedFile.url)}&embedded=true`}
                  title={selectedFile.name}
                  className="file-iframe"
                  frameBorder="0"
                  allowFullScreen
                />
              ) : (
                <div className="file-viewer-fallback">
                  <p>Preview not available for this file type.</p>
                  <a href={selectedFile.url} target="_blank" rel="noopener noreferrer" className="download-file-btn">
                    Download File
                  </a>
                </div>
              )}
            </div>
            <div className="file-viewer-footer">
              <a href={selectedFile.url} target="_blank" rel="noopener noreferrer" className="download-file-btn">
                Download File
              </a>
              <button className="close-viewer-btn" onClick={closeFileViewer}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Applications;